import { useState, useEffect, useRef } from "react";
import { Modal, TextInput, Textarea, Select, Switch, LoadingOverlay, Text, Group, FileButton, Box } from "@mantine/core";
import { Button, Alert } from "@xplatform/design-system";
import { IconUpload, IconX } from "@tabler/icons-react";
import { useTranslation } from "@hooks/useTranslation";
import { useDoctype } from "@hooks/flow";
import { useCategory } from "@hooks/useCategory";
import "./CreateDoctypeDialog.scss";

interface CreateDoctypeDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

export const CreateDoctypeDialog = ({ open, onClose, onSuccess }: CreateDoctypeDialogProps) => {
    const { t } = useTranslation();
    const { createDoctype, isLoading } = useDoctype();
    const { categories, fetchCategories } = useCategory();
    const [doctypeName, setDoctypeName] = useState("");
    const [doctypeCode, setDoctypeCode] = useState("");
    const [categoryId, setCategoryId] = useState<string | null>(null);
    const [description, setDescription] = useState("");
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const [mappingEnabled, setMappingEnabled] = useState(false);
    const [mappingService, setMappingService] = useState("");
    const [mappingPluginId, setMappingPluginId] = useState("");
    const [error, setError] = useState<string | null>(null);
    const resetRef = useRef<() => void>(null);

    useEffect(() => {
        if (open) {
            fetchCategories();
        }
    }, [open, fetchCategories]);

    const handleClose = () => {
        setDoctypeName("");
        setDoctypeCode("");
        setCategoryId(null);
        setDescription("");
        setLogoFile(null);
        setLogoPreview(null);
        setMappingEnabled(false);
        setMappingService("");
        setMappingPluginId("");
        setError(null);
        resetRef.current?.();
        onClose();
    };

    const handleSubmit = async () => {
        setError(null);

        if (!doctypeName.trim()) {
            setError("Document type name is required");
            return;
        }

        if (!doctypeCode.trim()) {
            setError("Document type code is required");
            return;
        }

        if (!categoryId) {
            setError("Category is required");
            return;
        }

        if (mappingEnabled && (!mappingService.trim() || !mappingPluginId.trim())) {
            setError("Mapping service and plugin ID are required when mapping is enabled");
            return;
        }

        try {
            const doctypeData = {
                docTypeName: doctypeName,
                docTypeCode: doctypeCode,
                categoryId,
                description: description || undefined,
                status: "active" as const,
                logo: logoFile || undefined,
                mappingEnabled,
                mappingService: mappingEnabled ? mappingService.trim() : null,
                mappingPluginId: mappingEnabled ? mappingPluginId.trim() : null,
            };

            await createDoctype(doctypeData);

            if (onSuccess) {
                onSuccess();
            }

            handleClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to create document type");
        }
    };

    const handleNameChange = (value: string) => {
        setDoctypeName(value);
        const code = value
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "");
        setDoctypeCode(code);
    };

    const handleLogoChange = (file: File | null) => {
        setLogoFile(file);
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        } else {
            setLogoPreview(null);
        }
    };

    const categoryData = categories.map((cat) => ({
        value: cat.id,
        label: cat.name,
    }));

    return (
        <Modal
            opened={open}
            onClose={handleClose}
            title={t("admin.documentTypes.actions.createDocType")}
            size="90%"
            className="create-doctype-dialog"
            closeButtonProps={{ disabled: isLoading, "aria-label": "Close" }}
            portalProps={{ target: '#admin-portal-root' }}
        >
            <LoadingOverlay visible={isLoading} />

            {error && (
                <Alert color="red" className="create-doctype-dialog__error" mb="md" icon={<IconX size={16} />}>
                    {error}
                </Alert>
            )}

            <div className="create-doctype-dialog__form">
                <TextInput
                    label={t("admin.table.columns.name")}
                    value={doctypeName}
                    onChange={(e) => handleNameChange(e.currentTarget.value)}
                    required
                    autoFocus
                />

                <TextInput
                    label={t("admin.table.columns.code")}
                    value={doctypeCode}
                    onChange={(e) => setDoctypeCode(e.currentTarget.value)}
                    required
                    description="Unique identifier (auto-generated from name)"
                />

                <Select
                    label={t("admin.categories.title")}
                    value={categoryId}
                    onChange={setCategoryId}
                    data={categoryData}
                    required
                    searchable
                    comboboxProps={{ withinPortal: false }}
                />

                <Textarea
                    label={t("admin.table.columns.description")}
                    value={description}
                    onChange={(e) => setDescription(e.currentTarget.value)}
                    rows={3}
                />

                <Box>
                    <Switch
                        label={t("admin.documentTypes.mapping.enable", {
                            defaultValue: "Enable mapping",
                        })}
                        description={t("admin.documentTypes.mapping.enableHint", {
                            defaultValue:
                                "Route completed items of this doctype to a mapping service.",
                        })}
                        checked={mappingEnabled}
                        onChange={(e) => setMappingEnabled(e.currentTarget.checked)}
                    />
                    {mappingEnabled && (
                        <Group grow mt="sm">
                            <TextInput
                                label={t("admin.documentTypes.mapping.service", {
                                    defaultValue: "Mapping service",
                                })}
                                placeholder="technical-manual"
                                value={mappingService}
                                onChange={(e) => setMappingService(e.currentTarget.value)}
                                required
                            />
                            <TextInput
                                label={t("admin.documentTypes.mapping.pluginId", {
                                    defaultValue: "Plugin ID",
                                })}
                                placeholder="rmi-topictree-tm-en"
                                value={mappingPluginId}
                                onChange={(e) => setMappingPluginId(e.currentTarget.value)}
                                required
                            />
                        </Group>
                    )}
                </Box>

                <Box>
                    <Text size="sm" fw={500} mb={4}>
                        Logo ({t("admin.documentTypes.labels.optional")})
                    </Text>
                    <FileButton
                        resetRef={resetRef}
                        onChange={handleLogoChange}
                        accept="image/*"
                    >
                        {(props) => (
                            <Button
                                variant="outline"
                                size="xs"
                                leftSection={<IconUpload size={14} />}
                                {...props}
                            >
                                {logoFile ? logoFile.name : t("admin.documentTypes.actions.uploadLogo")}
                            </Button>
                        )}
                    </FileButton>
                    {logoPreview && (
                        <Box mt="xs">
                            <img
                                src={logoPreview}
                                alt="Logo preview"
                                style={{ maxWidth: 100, maxHeight: 100, objectFit: "contain" }}
                            />
                        </Box>
                    )}
                </Box>
            </div>

            <Group justify="flex-end" mt="lg" className="create-doctype-dialog__actions">
                <Button variant="default" onClick={handleClose} disabled={isLoading}>
                    {t("common.cancel")}
                </Button>
                <Button onClick={handleSubmit} disabled={isLoading}>
                    {t("common.save")}
                </Button>
            </Group>
        </Modal>
    );
};
